import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ListIcon, SearchIcon, XMarkIcon, SpinnerIcon, ChevronDownIcon, BoltIcon, BedIcon, BathIcon, CarIcon, ArrowsPointingOutIcon, MapPinIcon, RulerMeasureIcon } from '../Icons'; 
import { AppUser, UserRole } from '../../types';
import { useConnection } from '../../context/ConnectionContext';
import { normalizeStringForSearch, formatCurrency, splitPropertyCodes } from '../../utils/formatters';
import { FilterPopover } from './FilterPopover';
import { PlaceAutocompleteSearch } from '../public/PlaceAutocompleteSearch';
import { SearchModeSelector } from './SearchModeSelector';
import { PriceFilterPopoverContent } from './filters/PriceFilterPopoverContent';
import { AreaFilterPopoverContent } from './filters/AreaFilterPopoverContent';
import { OwnerFilterPopoverContent } from './filters/OwnerFilterPopoverContent';

interface BrokerSearchAndFilterSectionProps {
  filters: any;
  onFilterChange: (field: string, value: any, applyImmediately?: boolean) => void;
  dynamicFilterOptions: any;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  appUser: AppUser | null;
  highlightLocation?: boolean;
  aiSearchQuery: string;
  setAiSearchQuery: (val: string) => void;
  isAiSearching: boolean;
  onAiSearch: (queryOverride?: string) => void;
  onCodeSearch: (codeOverride?: string) => void;
  onLocationSelect?: (lat: number, lon: number, displayName: string, geojson?: any) => void;
  mapSearchDisplayName?: string | null;
  onClearMapSearch?: () => void;
  spatialFilter: any | null;
  onClearSpatialFilter?: () => void;
  aiSearchError?: string | null;
  searchMode: 'ai' | 'code' | 'map';
  setSearchMode: (mode: 'ai' | 'code' | 'map') => void;
}

const FilterPill: React.FC<{ 
    label: string; 
    isActive: boolean; 
    onClick: () => void;
    onClear: (e: React.MouseEvent) => void;
}> = ({ label, isActive, onClick, onClear }) => (
    <button
        onClick={onClick}
        type="button"
        className={`group flex items-center gap-2 px-4 py-2 rounded-full border transition-all text-xs font-bold whitespace-nowrap shadow-sm h-10 flex-shrink-0 ${
            isActive 
            ? 'bg-[#00234F] text-white border-[#00234F]' 
            : 'bg-white text-gray-700 border-gray-200 hover:border-sky-300'
        }`}
    >
        <span className="truncate max-w-[120px] lg:max-w-[180px]">{label}</span>
        {isActive ? (
            <div onClick={onClear} className="p-0.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0">
                <XMarkIcon className="w-3.5 h-3.5 text-white" />
            </div>
        ) : (
            <ChevronDownIcon className="w-4 h-4 transition-transform flex-shrink-0 text-gray-400 group-hover:text-sky-500" />
        )}
    </button>
);

const InternalPopoverChip: React.FC<{ 
    label: string; 
    onRemove?: () => void; 
    colorClass?: string;
}> = ({ label, onRemove, colorClass = "bg-sky-50 text-sky-700 border-sky-100" }) => (
    <div className={`flex items-center px-2 py-1 rounded-full border text-[10px] font-black uppercase shadow-sm group ${colorClass}`}>
        <span className="truncate max-w-[120px]">{label}</span>
        {onRemove && (
            <button 
                type="button" 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }} 
                className="ml-1.5 p-0.5 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
            >
                <XMarkIcon className="w-3 h-3" />
            </button>
        )}
    </div>
);

export const BrokerSearchAndFilterSection: React.FC<BrokerSearchAndFilterSectionProps> = ({
  filters, onFilterChange, dynamicFilterOptions, onApplyFilters, onClearFilters,
  appUser, aiSearchQuery, setAiSearchQuery, isAiSearching, onAiSearch, onCodeSearch, onLocationSelect, mapSearchDisplayName, onClearMapSearch, spatialFilter, onClearSpatialFilter, aiSearchError,
  searchMode, setSearchMode
}) => {
  const { isOffline } = useConnection();
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const [localSearchText, setLocalSearchText] = useState(searchMode === 'ai' ? aiSearchQuery : filters.propertyCode);
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  
  // Hybrid Search Suggestions
  const [showMainSuggestions, setShowMainSuggestions] = useState(false);
  const mainSearchSuggestions = useMemo(() => {
    if (searchMode !== 'ai' || !localSearchText.trim() || localSearchText.length < 2) return [];
    
    const all = dynamicFilterOptions.allLocations || [];
    const normalizedInput = normalizeStringForSearch(localSearchText);
    const searchWords = normalizedInput.split(' ').filter(w => w.length > 0);
    
    return all
      .filter((l: any) => {
          const normalizedName = normalizeStringForSearch(l.name);
          return searchWords.every(word => normalizedName.includes(word));
      })
      .sort((a: any, b: any) => {
          const levelA = a.name.split(',').length; 
          const levelB = b.name.split(',').length;
          if (levelA !== levelB) return levelA - levelB;
          return a.name.localeCompare(b.name);
      })
      .slice(0, 5);
  }, [searchMode, localSearchText, dynamicFilterOptions.allLocations]);

  const selectLocationFromMain = (loc: any) => {
    const isNew = !filters.selectedUbicacion.includes(loc.name);
    const next = isNew ? [...filters.selectedUbicacion, loc.name] : filters.selectedUbicacion;
    
    setLocalSearchText('');
    setShowMainSuggestions(false);
    onFilterChange('selectedUbicacion', next, true);
  };

  const suggestionsRef = useRef<HTMLDivElement>(null);
  const prevActivePopoverRef = useRef<string | null>(null);

  useEffect(() => {
      if (searchMode === 'map' && mapSearchDisplayName) {
          setLocalSearchText(mapSearchDisplayName);
      } else {
          setLocalSearchText(searchMode === 'ai' ? aiSearchQuery : (searchMode === 'code' ? filters.propertyCode : ''));
      }
  }, [searchMode, aiSearchQuery, filters.propertyCode, mapSearchDisplayName]);

  const [locSearch, setLocSearch] = useState('');
  const [typeSearch, setTypeSearch] = useState('');

  useEffect(() => {
      if (prevActivePopoverRef.current !== null && activePopover !== prevActivePopoverRef.current) {
          onApplyFilters();
      }
      prevActivePopoverRef.current = activePopover;
  }, [activePopover, onApplyFilters]);

  const formatLocationDisplay = (fullName: string) => {
      const parts = fullName.split(',').map(p => p.trim());
      if (parts.length === 1) return { main: parts[0], type: 'CIUDAD' };
      if (parts.length === 2) return { main: `${parts[0]}, ${parts[1]}`, type: 'LOCALIDAD' };
      return { main: `${parts[0]}, ${parts[1]}, ${parts[2]}`, type: 'BARRIO' };
  };

  const filteredLocations = useMemo(() => {
    const all = dynamicFilterOptions.allLocations || [];
    if (!locSearch.trim()) return all.slice(0, 100);
    
    const normalizedInput = normalizeStringForSearch(locSearch);
    const searchWords = normalizedInput.split(' ').filter(w => w.length > 0);
    
    return all
      .filter((l: any) => {
          const normalizedName = normalizeStringForSearch(l.name);
          return searchWords.every(word => normalizedName.includes(word));
      })
      .sort((a: any, b: any) => {
          const levelA = a.name.split(',').length; 
          const levelB = b.name.split(',').length;
          if (levelA !== levelB) return levelA - levelB;
          return a.name.localeCompare(b.name);
      })
      .slice(0, 100);
  }, [dynamicFilterOptions.allLocations, locSearch]);

  const filteredTypes = useMemo(() => {
    const all = dynamicFilterOptions.availablePropertyTypes || [];
    if (!typeSearch.trim()) return all;
    const normalized = normalizeStringForSearch(typeSearch);
    return all.filter((t: any) => normalizeStringForSearch(t.name).includes(normalized));
  }, [dynamicFilterOptions.availablePropertyTypes, typeSearch]);

  const handleTogglePopover = (id: string) => {
      setActivePopover(activePopover === id ? null : id);
      setLocSearch('');
      setTypeSearch('');
      setHighlightedIdx(0);
  };

  const isClientRole = appUser?.role === UserRole.CLIENTE;

  const handleClearPill = (field: string, e: React.MouseEvent) => {
      e.stopPropagation();
      let newValue: any = [];
      if (field === 'selectedUbicacion' || field === 'selectedPropertyType' || field === 'selectedOwner') {
          newValue = [];
      } else if (field === 'priceRange' || field === 'areaRange') {
          newValue = { min: '', max: '' };
      } else if (field === 'espacios') {
          onFilterChange('selectedRooms', [], true);
          onFilterChange('selectedBaths', [], true);
          onFilterChange('selectedGarages', [], true);
          return;
      }
      onFilterChange(field, newValue, true);
  };

  const removeItemFromFilter = (field: string, item: any) => {
      const current = filters[field] || [];
      const next = current.filter((x: any) => x !== item);
      onFilterChange(field, next, true);
  };

  const formatPriceInput = (val: string) => {
      const numeric = val.replace(/\D/g, '');
      if (!numeric) return '';
      return new Intl.NumberFormat('es-CO').format(parseInt(numeric, 10));
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.selectedEstrato.length > 0) count++;
    if (filters.withElevator) count++;
    return count;
  }, [filters]);

  const propertyCodesList = useMemo(() => {
    return splitPropertyCodes(filters.propertyCode);
  }, [filters.propertyCode]);

  const handleRemoveCode = (codeToRemove: string) => {
    const newList = propertyCodesList.filter(c => c !== codeToRemove);
    const val = newList.join(', ');
    onFilterChange('propertyCode', val);
    onCodeSearch(val);
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent, list: any[], onSelect: (item: any) => void) => {
      if (list.length === 0) return;
      if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIdx(prev => (prev + 1) % list.length);
      } else if (e.key === 'ArrowUp') {
          setHighlightedIdx(prev => (prev - 1 + list.length) % list.length);
      } else if (e.key === 'Enter') {
          e.preventDefault();
          if (list[highlightedIdx]) onSelect(list[highlightedIdx]);
      }
  };

  const isInternalUser = appUser && (appUser.role === UserRole.COMERCIAL_INTERNO || appUser.role === UserRole.BROKER_INTERNO || appUser.role === UserRole.SUPERADMIN);
  const showOwnerFilter = isInternalUser || (appUser && appUser.role === UserRole.BROKER);

  const handleActionSearch = () => {
      if (searchMode === 'ai') {
          onAiSearch(localSearchText);
      } else {
          onFilterChange('propertyCode', localSearchText);
          onCodeSearch(localSearchText);
      }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <section className={`bg-white rounded-2xl shadow-xl border border-gray-100 relative z-[60] transition-all ${
        searchMode === 'ai' ? 'p-3 sm:p-6' : 'p-4 sm:p-6'
      }`}>
        <div className={searchMode === 'ai' ? 'mb-0 md:mb-3' : 'mb-3'}>
          <SearchModeSelector 
            searchMode={searchMode} 
            setSearchMode={setSearchMode} 
          />
        </div>

        {!(appUser?.role === UserRole.CLIENTE && searchMode === 'ai') && (
          <div className={`flex-col md:flex-row items-center gap-3 relative ${
            searchMode === 'ai' ? 'hidden md:flex' : 'flex'
          }`}>
            <div className="relative flex-grow w-full">
                {searchMode === 'map' ? (
                  <PlaceAutocompleteSearch 
                    onLocationSelect={(lat, lng, address) => {
                      if (onLocationSelect) onLocationSelect(lat, lng, address);
                    }}
                    placeholder="Ingresa una dirección o lugar (Google)..."
                  />
                ) : (
                  <>
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 z-10">
                        {searchMode === 'ai' ? <BoltIcon className="w-5 h-5" /> : <SearchIcon className="w-5 h-5" />}
                    </div>
                    <input
                        type="text"
                        value={localSearchText}
                        onChange={(e) => {
                          setLocalSearchText(e.target.value);
                          if (searchMode === 'ai') setShowMainSuggestions(true);
                        }}
                        placeholder={
                            searchMode === 'ai' 
                            ? "Ej: Apartamento en Chapinero con balcón..." 
                            : "Ingresa códigos separados por coma o espacio..."
                        }
                        className="block w-full pl-11 pr-12 py-3.5 border-2 border-gray-100 rounded-2xl focus:ring-2 focus:ring-sky-200 focus:border-sky-500 outline-none transition-all text-sm shadow-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (showMainSuggestions && mainSearchSuggestions.length > 0) {
                              handleActionSearch();
                            } else {
                              handleActionSearch();
                            }
                            setShowMainSuggestions(false);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowMainSuggestions(false), 200);
                        }}
                        onFocus={() => {
                          if (searchMode === 'ai' && localSearchText.length >= 2) setShowMainSuggestions(true);
                        }}
                    />
                    
                    {/* Hybrid Search Suggestions Dropdown */}
                    {showMainSuggestions && mainSearchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl overflow-hidden z-[100] border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200">
                          <div className="p-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sugerencias de Ubicación</span>
                              <button onClick={() => setShowMainSuggestions(false)} className="text-gray-400 hover:text-gray-600">
                                  <XMarkIcon className="w-4 h-4" />
                              </button>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                              {mainSearchSuggestions.map((loc) => {
                                  const { main, type } = formatLocationDisplay(loc.name);
                                  return (
                                    <button
                                        key={loc.id}
                                        onClick={() => selectLocationFromMain(loc)}
                                        className="w-full flex items-center px-4 py-3 hover:bg-sky-50 transition-colors text-left group"
                                    >
                                        <MapPinIcon className="w-4 h-4 text-gray-300 group-hover:text-sky-500 mr-3" />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-gray-700">{main}</span>
                                            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-tighter">{type} • Agregar filtro</span>
                                        </div>
                                    </button>
                                  );
                              })}
                          </div>
                      </div>
                    )}
                    {localSearchText && (
                        <button onClick={() => {
                          setLocalSearchText('');
                          if (searchMode === 'code') { onFilterChange('propertyCode', ''); onCodeSearch(''); }
                        }} className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 z-10">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    )}
                  </>
                )}
            </div>
            {searchMode !== 'map' && (
                <button type="button" onClick={handleActionSearch} disabled={isAiSearching || isOffline} className="w-full md:w-auto px-10 py-3.5 bg-[#0073b7] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl hover:bg-sky-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2">
                    {isAiSearching ? <SpinnerIcon className="w-5 h-5 mr-2" /> : <SearchIcon className="w-5 h-5 mr-2" />}
                    <span>{isAiSearching ? 'Buscando...' : 'Buscar'}</span>
                </button>
            )}
          </div>
        )}

        {searchMode === 'map' && mapSearchDisplayName && (
          <div className="flex flex-wrap gap-2 mt-4 animate-fade-in">
             <div className="flex items-center bg-sky-100 text-sky-800 px-4 py-2 rounded-full border border-sky-200 text-xs font-black shadow-sm group">
                <MapPinIcon className="w-4 h-4 mr-2 text-sky-600" />
                <span className="truncate max-w-[200px] sm:max-w-md uppercase tracking-tight">{mapSearchDisplayName}</span>
                <button onClick={onClearMapSearch} className="ml-3 p-1 hover:bg-sky-200 rounded-full transition-colors text-sky-500 hover:text-red-600"><XMarkIcon className="w-4 h-4" /></button>
             </div>
          </div>
        )}

        {searchMode === 'code' && propertyCodesList.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 animate-fade-in">
             <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest w-full mb-1 ml-1">Códigos Buscados:</span>
             {propertyCodesList.map((code) => (
               <div key={code} className="flex items-center bg-sky-50 text-sky-700 px-3 py-1.5 rounded-full border border-sky-100 text-xs font-bold shadow-sm">
                  <span>{code}</span><button onClick={() => handleRemoveCode(code)} className="ml-2 p-0.5 hover:bg-sky-200 rounded-full transition-colors text-sky-400 hover:text-red-500"><XMarkIcon className="w-3.5 h-3.5" /></button>
               </div>
             ))}
             <button onClick={() => { onFilterChange('propertyCode', ''); onCodeSearch(''); setLocalSearchText(''); }} className="text-[10px] font-black text-red-500 hover:text-red-700 uppercase tracking-widest ml-2 self-center transition-colors">Limpiar todos</button>
          </div>
        )}
      </section>

      {/* Barra de Filtros Rápidos (Ubicación, Tipo, Precio, Área, Habitaciones, etc.) */}
      {searchMode === 'ai' && (
        <section className={`relative transition-all animate-fade-in pt-1 sm:pt-2 ${activePopover ? 'z-[90]' : 'z-40'}`}>
            <div className="flex items-center gap-2 pb-2 overflow-x-auto md:overflow-visible scrollbar-hide flex-nowrap md:flex-wrap -mx-3 px-3 sm:mx-0 sm:px-0">
                {/* 1. Ubicacion */}
                <div className={`relative flex-shrink-0 ${activePopover === 'ubicacion' ? 'z-50' : 'z-10'}`}>
                    <FilterPill 
                        label={filters.selectedUbicacion.length > 0 ? filters.selectedUbicacion.join(', ') : "Ubicación"} 
                        isActive={filters.selectedUbicacion.length > 0} 
                        onClick={() => handleTogglePopover('ubicacion')} 
                        onClear={(e) => handleClearPill('selectedUbicacion', e)} 
                    />
                    <FilterPopover isOpen={activePopover === 'ubicacion'} onClose={() => setActivePopover(null)} title="Búsqueda de Ubicaciones" align="left">
                        <div className="space-y-4">
                            <div className="relative">
                                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" autoFocus value={locSearch} onChange={(e) => setLocSearch(e.target.value)} onKeyDown={(e) => handlePopoverKeyDown(e, filteredLocations, (loc) => { const next = filters.selectedUbicacion.includes(loc.name) ? filters.selectedUbicacion.filter((n: string) => n !== loc.name) : [...filters.selectedUbicacion, loc.name]; onFilterChange('selectedUbicacion', next); })} placeholder="Busca ciudad, barrio o localidad..." className="w-full pl-9 pr-10 py-2.5 text-sm border-gray-200 rounded-xl focus:ring-sky-500 focus:border-sky-500" />
                                {locSearch && <button onClick={() => setLocSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><XMarkIcon className="w-4 h-4" /></button>}
                            </div>
                            <div className="max-h-64 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                {filteredLocations.map((loc: any, idx: number) => {
                                    const { main, type } = formatLocationDisplay(loc.name);
                                    const isChecked = filters.selectedUbicacion.includes(loc.name);
                                    return (
                                      <label key={loc.id} className={`flex items-center p-2.5 rounded-xl cursor-pointer group transition-colors border-2 ${idx === highlightedIdx ? 'bg-sky-100 border-sky-400 shadow-sm' : 'hover:bg-sky-50 border-transparent'}`}>
                                        <input 
                                          type="checkbox" 
                                          checked={isChecked} 
                                          onChange={() => { 
                                            const next = filters.selectedUbicacion.includes(loc.name) 
                                              ? filters.selectedUbicacion.filter((n: string) => n !== loc.name) 
                                              : [...filters.selectedUbicacion, loc.name]; 
                                            onFilterChange('selectedUbicacion', next); 
                                          }} 
                                          className="rounded border-gray-300 text-sky-600 focus:ring-sky-500 mr-3" 
                                        />
                                        <div className="flex flex-col min-w-0">
                                          <span className="text-sm font-medium text-gray-700 truncate">{main}</span>
                                          <span className="text-[10px] text-gray-400 font-bold uppercase">({type})</span>
                                        </div>
                                      </label>
                                    );
                                })}
                            </div>
                            {filters.selectedUbicacion.length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-50">
                                    {filters.selectedUbicacion.map((item: string) => (
                                        <InternalPopoverChip 
                                          key={item} 
                                          label={item} 
                                          onRemove={() => removeItemFromFilter('selectedUbicacion', item)} 
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </FilterPopover>
                </div>

                {/* 2. Tipo */}
                <div className={`relative flex-shrink-0 ${activePopover === 'tipo' ? 'z-50' : 'z-10'}`}>
                    <FilterPill 
                        label={filters.selectedPropertyType.length > 0 ? filters.selectedPropertyType.join(', ') : "Tipo Inmueble"} 
                        isActive={filters.selectedPropertyType.length > 0} 
                        onClick={() => handleTogglePopover('tipo')} 
                        onClear={(e) => handleClearPill('selectedPropertyType', e)} 
                    />
                    <FilterPopover isOpen={activePopover === 'tipo'} onClose={() => setActivePopover(null)} title="Tipos de Inmueble" align="left">
                        <div className="space-y-4">
                            <div className="relative">
                                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" autoFocus value={typeSearch} onChange={(e) => setTypeSearch(e.target.value)} onKeyDown={(e) => handlePopoverKeyDown(e, filteredTypes, (t) => { const next = filters.selectedPropertyType.includes(t.name) ? filters.selectedPropertyType.filter((n: string) => n !== t.name) : [...filters.selectedPropertyType, t.name]; onFilterChange('selectedPropertyType', next); })} placeholder="Ej: Apartamento..." className="w-full pl-9 pr-10 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 shadow-sm" />
                                {typeSearch && <button onClick={() => setTypeSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><XMarkIcon className="w-4 h-4" /></button>}
                            </div>
                            <div className="max-h-60 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                {filteredTypes.map((t: any, idx: number) => {
                                  const isChecked = filters.selectedPropertyType.includes(t.name);
                                  return (
                                    <label key={t.id} className={`flex items-center p-2.5 rounded-xl cursor-pointer text-sm transition-colors border-2 ${idx === highlightedIdx ? 'bg-sky-100 border-sky-400 shadow-sm' : 'hover:bg-sky-50 border-transparent'}`}>
                                      <input 
                                        type="checkbox" 
                                        checked={isChecked} 
                                        onChange={() => { 
                                          const next = filters.selectedPropertyType.includes(t.name) 
                                            ? filters.selectedPropertyType.filter((n: string) => n !== t.name) 
                                            : [...filters.selectedPropertyType, t.name]; 
                                          onFilterChange('selectedPropertyType', next); 
                                        }} 
                                        className="rounded border-gray-300 text-sky-600 mr-3" 
                                      />
                                      <span className="text-gray-700 font-medium">{t.name}</span>
                                    </label>
                                  );
                                })}
                            </div>
                            {filters.selectedPropertyType.length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-50">
                                    {filters.selectedPropertyType.map((item: string) => (
                                        <InternalPopoverChip 
                                          key={item} 
                                          label={item} 
                                          onRemove={() => removeItemFromFilter('selectedPropertyType', item)} 
                                          colorClass="bg-purple-50 text-purple-700 border-purple-100" 
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </FilterPopover>
                </div>

                {/* 3. Precio */}
                <div className={`relative flex-shrink-0 ${activePopover === 'precio' ? 'z-50' : 'z-10'}`}>
                    <FilterPill 
                        label={
                            filters.priceRange.min || filters.priceRange.max
                            ? `${filters.priceRange.min ? formatCurrency(parseInt(filters.priceRange.min)) : '$0'} - ${filters.priceRange.max ? formatCurrency(parseInt(filters.priceRange.max)) : 'Max'}`
                            : "Precio"
                        }
                        isActive={!!filters.priceRange.min || !!filters.priceRange.max} 
                        onClick={() => handleTogglePopover('precio')} 
                        onClear={(e) => handleClearPill('priceRange', e)} 
                    />
                    <FilterPopover isOpen={activePopover === 'precio'} onClose={() => setActivePopover(null)} title="Presupuesto" align="left">
                        <PriceFilterPopoverContent 
                            priceRange={filters.priceRange} 
                            onFilterChange={onFilterChange} 
                        />
                    </FilterPopover>
                </div>

                {/* 4. Area */}
                <div className={`relative flex-shrink-0 ${activePopover === 'area' ? 'z-50' : 'z-10'}`}>
                    <FilterPill 
                        label={
                            filters.areaRange.min || filters.areaRange.max
                            ? `${filters.areaRange.min || '0'} - ${filters.areaRange.max || 'Max'} m²`
                            : "Área"
                        }
                        isActive={!!filters.areaRange.min || !!filters.areaRange.max} 
                        onClick={() => handleTogglePopover('area')} 
                        onClear={(e) => handleClearPill('areaRange', e)} 
                    />
                    <FilterPopover isOpen={activePopover === 'area'} onClose={() => setActivePopover(null)} title="Área Construida (m²)" align="left">
                        <AreaFilterPopoverContent 
                            areaRange={filters.areaRange} 
                            onFilterChange={onFilterChange} 
                        />
                    </FilterPopover>
                </div>

                {/* 5. Espacios */}
                <div className={`relative flex-shrink-0 ${activePopover === 'espacios' ? 'z-50' : 'z-10'}`}>
                    <FilterPill 
                        label={
                            filters.selectedRooms.length > 0 || filters.selectedBaths.length > 0 || filters.selectedGarages.length > 0
                            ? [
                                filters.selectedRooms.length > 0 ? `${Math.min(...filters.selectedRooms)}+ Habs` : null,
                                filters.selectedBaths.length > 0 ? `${Math.min(...filters.selectedBaths)}+ Baños` : null,
                                filters.selectedGarages.length > 0 ? `${Math.min(...filters.selectedGarages)}+ Parq` : null
                            ].filter(Boolean).join(' / ')
                            : "Espacios"
                        }
                        isActive={filters.selectedRooms.length > 0 || filters.selectedBaths.length > 0 || filters.selectedGarages.length > 0} 
                        onClick={() => handleTogglePopover('espacios')} 
                        onClear={(e) => handleClearPill('espacios', e)} 
                    />
                    <FilterPopover isOpen={activePopover === 'espacios'} onClose={() => setActivePopover(null)} title="Distribución" align="left">
                        <div className="space-y-5">
                            {[{ label: 'Habitaciones', icon: <BedIcon className="w-4 h-4 text-sky-500"/>, key: 'selectedRooms' }, { label: 'Baños', icon: <BathIcon className="w-4 h-4 text-sky-500"/>, key: 'selectedBaths' }, { label: 'Parqueaderos', icon: <CarIcon className="w-4 h-4 text-sky-500"/>, key: 'selectedGarages' }].map((item) => (
                                <div key={item.key}>
                                    <div className="flex items-center space-x-2 mb-3">{item.icon}<p className="text-[11px] font-black text-gray-700 uppercase tracking-tight">{item.label}</p></div>
                                    <div className="flex gap-2">{[1,2,3,4,5].map(n => <button key={n} onClick={() => { const current = filters[item.key] || []; const next = current.includes(n) ? current.filter((x:any)=>x!==n) : [...current, n]; onFilterChange(item.key, next); }} className={`w-10 h-10 rounded-xl border text-sm font-bold transition-all ${filters[item.key]?.includes(n) ? 'bg-[#00234F] border-[#00234F] text-white' : 'bg-white border-gray-100 text-gray-500 hover:border-sky-200'}`}>{n === 5 ? '5+' : n}</button>)}</div>
                                </div>
                            ))}
                            {(filters.selectedRooms.length > 0 || filters.selectedBaths.length > 0 || filters.selectedGarages.length > 0) && (
                                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                                    {filters.selectedRooms.map((n: number) => <InternalPopoverChip key={`r-${n}`} label={`${n === 5 ? '5+' : n} Hab`} onRemove={() => removeItemFromFilter('selectedRooms', n)} colorClass="bg-amber-50 text-amber-700 border-amber-100" />)}
                                    {filters.selectedBaths.map((n: number) => <InternalPopoverChip key={`b-${n}`} label={`${n === 5 ? '5+' : n} Baños`} onRemove={() => removeItemFromFilter('selectedBaths', n)} colorClass="bg-sky-50 text-sky-700 border-sky-100" />)}
                                    {filters.selectedGarages.map((n: number) => <InternalPopoverChip key={`g-${n}`} label={`${n === 5 ? '5+' : n} Parq`} onRemove={() => removeItemFromFilter('selectedGarages', n)} colorClass="bg-gray-50 text-gray-700 border-gray-100" />)}
                                </div>
                            )}
                        </div>
                    </FilterPopover>
                </div>

                {/* 6. Dueño (Solo Internos u Broker) */}
                {showOwnerFilter && (
                    <div className={`relative flex-shrink-0 ${activePopover === 'owner' ? 'z-50' : 'z-10'}`}>
                        <FilterPill 
                            label={filters.selectedOwner.length > 0 ? filters.selectedOwner.join(', ') : "Fuente / Dueño"} 
                            isActive={filters.selectedOwner.length > 0} 
                            onClick={() => handleTogglePopover('owner')} 
                            onClear={(e) => handleClearPill('selectedOwner', e)} 
                        />
                        <FilterPopover isOpen={activePopover === 'owner'} onClose={() => setActivePopover(null)} title="Fuente de Información" align="right">
                            <OwnerFilterPopoverContent
                                availableOwners={dynamicFilterOptions.availableOwners || []}
                                selectedOwners={filters.selectedOwner || []}
                                onFilterChange={onFilterChange}
                            />
                        </FilterPopover>
                    </div>
                )}

                {/* ✨ Lo más reciente */}
                <div className="relative flex-shrink-0 z-10">
                    <button
                        type="button"
                        onClick={() => {
                            onFilterChange('onlyRecent', !filters.onlyRecent, true);
                        }}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all text-xs font-bold whitespace-nowrap shadow-sm h-10 flex-shrink-0 ${
                            filters.onlyRecent 
                            ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600' 
                            : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50/30'
                        }`}
                    >
                        <span>✨ Lo más reciente</span>
                        {filters.onlyRecent && (
                            <div 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    onFilterChange('onlyRecent', false, true); 
                                }} 
                                className="ml-1 p-0.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
                            >
                                <XMarkIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                        )}
                    </button>
                </div>

                {/* 7. Mas */}
                <div className={`relative flex-shrink-0 ${activePopover === 'mas' ? 'z-50' : 'z-10'}`}>
                    <button 
                    type="button" 
                    onClick={() => handleTogglePopover('mas')} 
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all text-xs font-bold whitespace-nowrap shadow-sm h-10 flex-shrink-0 ${
                        activeFiltersCount > 0 
                        ? 'bg-[#00234F] text-white border-[#00234F]' 
                        : 'bg-white text-gray-700 border-gray-200 hover:border-sky-300'
                    }`}
                    >
                        <ListIcon className="w-5 h-5 mr-1" />
                        <span className="truncate max-w-[120px]">
                        {activeFiltersCount > 0 
                            ? [
                                filters.selectedEstrato.length > 0 ? `Estrato ${filters.selectedEstrato.join(', ')}` : null,
                                filters.withElevator ? "Ascensor" : null
                            ].filter(Boolean).join(', ')
                            : "Más filtros"
                        }
                        </span>
                        {activeFiltersCount > 0 && <span className="flex items-center justify-center bg-sky-400 text-white text-[10px] rounded-full w-4 h-4 ml-1 font-bold">{activeFiltersCount}</span>}
                        {activeFiltersCount > 0 ? (
                            <div onClick={(e) => { e.stopPropagation(); onClearFilters(); }} className="ml-1 p-0.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0">
                                <XMarkIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                        ) : (
                            <ChevronDownIcon className="w-4 h-4 transition-transform flex-shrink-0 text-gray-400" />
                        )}
                    </button>
                    <FilterPopover isOpen={activePopover === 'mas'} onClose={() => setActivePopover(null)} title="Adicionales" align="right">
                        <div className="space-y-6">
                            <div><p className="text-[10px] font-bold text-gray-500 uppercase mb-3">Estrato</p><div className="flex flex-wrap gap-2">{[1,2,3,4,5,6].map(n => <button key={n} type="button" onClick={() => { const next = filters.selectedEstrato.includes(n) ? filters.selectedEstrato.filter((x:any)=>x!==n) : [...filters.selectedEstrato, n]; onFilterChange('selectedEstrato', next); }} className={`w-10 h-10 rounded-xl border text-sm font-bold transition-colors ${filters.selectedEstrato.includes(n) ? 'bg-[#00234F] border-[#00234F] text-white' : 'border-gray-200 text-gray-600'}`}>{n}</button>)}</div></div>
                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer border border-gray-100"><span className="text-xs text-gray-700 font-bold uppercase">Con Ascensor</span><input type="checkbox" checked={filters.withElevator} onChange={(e) => onFilterChange('withElevator', e.target.checked)} className="h-6 w-6 text-sky-600 rounded-lg border-gray-300 focus:ring-sky-500" /></label>
                            
                            {(filters.selectedEstrato.length > 0 || filters.withElevator) && (
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-50">
                                    {filters.selectedEstrato.map((n: number) => <InternalPopoverChip key={`e-${n}`} label={`Estrato ${n}`} onRemove={() => removeItemFromFilter('selectedEstrato', n)} colorClass="bg-orange-50 text-orange-700 border-orange-100" />)}
                                    {filters.withElevator && <InternalPopoverChip label="Con Ascensor" onRemove={() => onFilterChange('withElevator', false, true)} colorClass="bg-indigo-50 text-indigo-700 border-indigo-100" />}
                                </div>
                            )}

                            <div className="flex flex-col gap-2 pt-2"><button type="button" onClick={() => { onApplyFilters(); setActivePopover(null); }} className="w-full py-3 bg-[#0073b7] text-white rounded-xl font-bold text-sm shadow-md hover:bg-sky-700 uppercase tracking-wide">Aplicar</button><button type="button" onClick={() => { onClearFilters(); setActivePopover(null); }} className="w-full py-2 text-gray-500 text-xs font-bold uppercase hover:text-gray-800">Limpiar todos</button></div>
                        </div>
                    </FilterPopover>
                </div>

                {spatialFilter && (
                    <button type="button" className="flex items-center space-x-2 px-4 py-2 rounded-full border border-transparent transition-all text-xs font-bold whitespace-nowrap shadow-sm bg-green-600 text-white hover:bg-green-700 h-10 flex-shrink-0">
                        <MapPinIcon className="w-4 h-4 text-white" />
                        <span className="uppercase tracking-wide transition-all">Dibujo Mapa</span>
                        <div onClick={(e) => { e.stopPropagation(); if(onClearSpatialFilter) onClearSpatialFilter(); }} className="p-0.5 bg-white/20 hover:bg-white/40 rounded-full transition-colors flex items-center justify-center">
                            <XMarkIcon className="w-3.5 h-3.5 text-white" />
                        </div>
                    </button>
                )}
                
                {/* Clear All Global */}
                {(filters.selectedUbicacion.length > 0 || filters.selectedPropertyType.length > 0 || filters.selectedOwner.length > 0 || filters.priceRange.min || filters.priceRange.max || filters.areaRange.min || filters.areaRange.max || filters.selectedRooms.length > 0 || filters.selectedBaths.length > 0 || filters.selectedGarages.length > 0 || activeFiltersCount > 0 || filters.onlyRecent) && (
                  <button 
                      onClick={onClearFilters}
                      className="text-[10px] font-black text-red-500 hover:text-red-700 uppercase tracking-[0.1em] px-2 py-2 border border-transparent hover:border-red-100 rounded-xl transition-all h-10 flex items-center flex-shrink-0 whitespace-nowrap"
                  >
                      Limpiar todos
                  </button>
                )}
            </div>
        </section>
      )}

    </div>
  );
};